<template>
  <div class="task-list">
    <div class="list-header">
      <div class="header-left">
        <h2 class="page-title">我的挂宝任务</h2>
        <span class="task-count">共 {{ tasks.length }} 个任务</span>
      </div>
      <div class="header-actions">
        <button @click="refreshTasks" class="refresh-btn">
          刷新列表
        </button>
        <button @click="createNewTask" class="create-btn">
          创建新任务
        </button>
      </div>
    </div>

    <!-- 任务列表 -->
    <div class="tasks-container">
      <div 
        v-for="task in tasks" 
        :key="task.id"
        class="task-card"
        :class="{ 'active': task.status === 'running' }"
      >
        <div class="task-header">
          <div class="game-info">
            <img :src="getImageUrl(task.game.imagePath)" :alt="task.game.name">
            <h3>{{ task.game.name }}</h3>
          </div>
          <span class="status-badge" :class="task.status">
            {{ getStatusText(task.status) }}
          </span>
        </div>

        <div class="task-info">
          <div class="info-grid">
            <div class="info-item">
              <span class="label">账号:</span>
              <span class="value">{{ task.account }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间:</span>
              <span class="value">{{ formatDate(task.createTime) }}</span>
            </div>
            <div class="info-item">
              <span class="label">到期时间:</span>
              <span class="value">{{ formatDate(task.expireTime) }}</span>
            </div>
            <div class="info-item">
              <span class="label">剩余时间:</span>
              <span class="value">{{ getRemainingTime(task.expireTime) }}</span>
            </div>
          </div>

          <div class="progress-info" v-if="task.status === 'running'">
            <div class="progress-text">
              <span>任务进度</span>
              <span>{{ task.progress }}%</span>
            </div>
            <div class="progress-bar">
              <div 
                class="progress-fill"
                :style="{ width: `${task.progress}%` }"
              ></div>
            </div>
          </div>
        </div>

        <div class="task-actions">
          <button 
            v-if="task.status === 'stopped'"
            @click="startTask(task.id)" 
            class="action-btn start"
          >
            启动任务
          </button>
          <button 
            v-else-if="task.status === 'running'"
            @click="stopTask(task.id)" 
            class="action-btn stop"
          >
            停止任务
          </button>
          <button 
            @click="deleteTask(task.id)" 
            class="action-btn delete"
          >
            删除任务
          </button>
        </div>
      </div>

      <!-- 空状态展示 -->
      <div v-if="!tasks.length" class="empty-state">
        <span class="empty-icon">📋</span>
        <p>暂无任务数据</p>
        <button @click="createNewTask" class="create-btn">
          立即创建任务
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const tasks = ref([])
const isLoading = ref(false)

// UUID生成函数
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// 获取任务列表
const fetchTasks = async () => {
  try {
    isLoading.value = true
    // TODO: 替换为实际的API调用
    const user_id = localStorage.getItem("user_id")
    if(user_id){
      const response = await fetch('/api/tasks/list?user_id=' + user_id)
      const data = await response.json()
      tasks.value = data.tasks
    }else{
      localStorage.setItem("user_id", generateUUID())
      tasks.value = []
    }
  } catch (error) {
    console.error('获取任务列表失败:', error)
  } finally {
    isLoading.value = false
  }
}

// 刷新任务列表
const refreshTasks = () => {
  fetchTasks()
}

// 创建新任务
const createNewTask = () => {
  router.push('/dashboard/autotask/create')
}

// 启动任务
const startTask = async (taskId) => {
  try {
    // TODO: 实现启动任务逻辑
    await fetch(`/api/tasks/${taskId}/start`, { method: 'POST' })
    await fetchTasks()
  } catch (error) {
    console.error('启动任务失败:', error)
  }
}

// 停止任务
const stopTask = async (taskId) => {
  try {
    // TODO: 实现停止任务逻辑
    await fetch(`/api/tasks/${taskId}/stop`, { method: 'POST' })
    await fetchTasks()
  } catch (error) {
    console.error('停止任务失败:', error)
  }
}

// 删除任务
const deleteTask = async (taskId) => {
  if (!confirm('确定要删除此任务吗？')) return

  try {
    // TODO: 实现删除任务逻辑
    await fetch(`/api/tasks/${taskId}`, { method: 'DELETE' })
    await fetchTasks()
  } catch (error) {
    console.error('删除任务失败:', error)
  }
}

// 获取游戏图片URL
const getImageUrl = (filename) => {
  return `${filename}`
}

// 获取状态文本
const getStatusText = (status) => {
  const statusMap = {
    running: '运行中',
    stopped: '已停止',
    expired: '已过期',
    error: '异常'
  }
  return statusMap[status] || status
}

// 格式化日期
const formatDate = (date) => {
  return new Date(date).toLocaleString()
}

// 计算剩余时间
const getRemainingTime = (expireTime) => {
  const now = new Date()
  const expire = new Date(expireTime)
  const diff = expire - now

  if (diff <= 0) return '已过期'

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  
  return `${days}天${hours}小时`
}

onMounted(() => {
  fetchTasks()
})
</script>

<style scoped>
.task-list {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.page-title {
  font-size: 24px;
  font-weight: 500;
  color: white;
  margin: 0;
}

.task-count {
  color: #a8a4b4;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.refresh-btn,
.create-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn {
  background-color: #2d2640;
  color: white;
  border: 1px solid #3d3654;
}

.refresh-btn:hover {
  background-color: #3d3654;
}

.create-btn {
  background-color: #7c3aed;
  color: white;
  border: none;
}

.create-btn:hover {
  background-color: #6d28d9;
}

.tasks-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
}

.task-card {
  background-color: #211d2d;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.task-card.active {
  border: 2px solid #7c3aed;
}

.task-header {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #3d3654;
}

.game-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.game-info img {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
}

.game-info h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.running {
  background-color: #059669;
  color: white;
}

.status-badge.stopped {
  background-color: #6b7280;
  color: white;
}

.status-badge.expired {
  background-color: #dc2626;
  color: white;
}

.status-badge.error {
  background-color: #dc2626;
  color: white;
}

.task-info {
  padding: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.info-item {
  display: flex;
  gap: 8px;
}

.info-item .label {
  color: #a8a4b4;
  min-width: 70px;
}

.info-item .value {
  color: white;
}

.progress-info {
  margin-top: 12px;
}

.progress-text {
  display: flex;
  justify-content: space-between;
  color: #a8a4b4;
  font-size: 14px;
  margin-bottom: 8px;
}

.progress-bar {
  height: 6px;
  background-color: #2d2640;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #7c3aed;
  transition: width 0.3s ease;
}

.task-actions {
  padding: 16px;
  display: flex;
  gap: 8px;
  border-top: 1px solid #3d3654;
}

.action-btn {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.action-btn:hover {
  opacity: 0.9;
}

.action-btn.start {
  background-color: #059669;
  color: white;
}

.action-btn.stop {
  background-color: #6b7280;
  color: white;
}

.action-btn.delete {
  background-color: #dc2626;
  color: white;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 48px;
  background-color: #211d2d;
  border-radius: 8px;
  color: #a8a4b4;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

@media (max-width: 768px) {
  .task-list {
    padding: 16px;
  }

  .list-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .header-actions {
    justify-content: stretch;
  }

  .header-actions button {
    flex: 1;
  }

  .tasks-container {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>